import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scan, Shield, AlertTriangle, CheckCircle, XCircle, Clock, 
  Play, Trash2, Plus, Server, Globe, Lock, ArrowRight, 
  RefreshCw, Target, Activity, ChevronDown, ChevronRight,
  FileText, Download, Eye
} from 'lucide-react';
import { pentestAPI } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';

const severityColors = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
  info: 'bg-gray-500 text-white'
};

const statusColors = {
  pending: 'bg-slate-500',
  running: 'bg-blue-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500'
};

const PentestDashboard = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewScan, setShowNewScan] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [quickScanTarget, setQuickScanTarget] = useState('');
  const [quickScanLoading, setQuickScanLoading] = useState(false);
  const [quickScanResult, setQuickScanResult] = useState(null);

  // New scan form
  const [newScan, setNewScan] = useState({
    name: '',
    target: '',
    targetType: 'url',
    scanType: 'quick',
    portScan: true,
    headerCheck: true,
    bannerGrab: true,
    sslCheck: true
  });

  useEffect(() => {
    loadData();
    // Poll for running scans
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [scansRes, statsRes] = await Promise.all([
        pentestAPI.getScans(),
        pentestAPI.getStats()
      ]);
      setScans(scansRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScan = async () => {
    try {
      const response = await pentestAPI.createScan(newScan);
      setScans([response.data, ...scans]);
      setShowNewScan(false);
      setNewScan({
        name: '',
        target: '',
        targetType: 'url',
        scanType: 'quick',
        portScan: true,
        headerCheck: true,
        bannerGrab: true,
        sslCheck: true
      });
    } catch (error) {
      console.error('Error creating scan:', error);
    }
  };

  const handleStartScan = async (scanId) => {
    try {
      await pentestAPI.startScan(scanId);
      loadData();
    } catch (error) {
      console.error('Error starting scan:', error);
    }
  };

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this scan?')) return;
    try {
      await pentestAPI.deleteScan(scanId);
      setScans(scans.filter(s => s.id !== scanId));
      if (selectedScan?.id === scanId) {
        setSelectedScan(null);
      }
    } catch (error) {
      console.error('Error deleting scan:', error);
    }
  };

  const handleQuickScan = async () => {
    if (!quickScanTarget) return;
    setQuickScanLoading(true);
    setQuickScanResult(null);
    try {
      const response = await pentestAPI.quickScan({ target: quickScanTarget });
      setQuickScanResult(response.data);
    } catch (error) {
      console.error('Error running quick scan:', error);
    } finally {
      setQuickScanLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Scan className="w-6 h-6 text-white" />
              </div>
              Penetration Testing
            </h1>
            <p className="text-slate-400 mt-2">Scan and identify vulnerabilities in your systems</p>
          </div>
          <button
            onClick={() => setShowNewScan(true)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Scan
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total Scans</p>
                  <p className="text-2xl font-bold text-white">{stats.totalScans}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Completed</p>
                  <p className="text-2xl font-bold text-white">{stats.completedScans}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Critical Vulns</p>
                  <p className="text-2xl font-bold text-white">{stats.criticalVulnerabilities}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">High Vulns</p>
                  <p className="text-2xl font-bold text-white">{stats.highVulnerabilities}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Scan Section */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Quick Scan
          </h2>
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={quickScanTarget}
                onChange={(e) => setQuickScanTarget(e.target.value)}
                placeholder="Enter URL or hostname (e.g., https://example.com)"
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <button
              onClick={handleQuickScan}
              disabled={quickScanLoading || !quickScanTarget}
              className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              {quickScanLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Scan className="w-4 h-4" />
              )}
              Scan
            </button>
          </div>

          {/* Quick Scan Results */}
          {quickScanResult && (
            <div className="mt-4 p-4 bg-slate-700/50 rounded-lg">
              <h3 className="text-white font-medium mb-3">Quick Scan Results for: {quickScanResult.target}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Open Ports */}
                <div>
                  <p className="text-slate-400 text-sm mb-2">Open Ports ({quickScanResult.openPorts?.length || 0}):</p>
                  {quickScanResult.openPorts?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {quickScanResult.openPorts.map((p, i) => (
                        <span key={i} className={`px-2 py-1 rounded text-sm ${
                          p.source === 'shodan' ? 'bg-violet-500/20 text-violet-400' : 'bg-green-500/20 text-green-400'
                        }`}>
                          {p.port} ({p.service})
                          {p.source === 'shodan' && <span className="ml-1 text-xs">🔍</span>}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No open ports found</p>
                  )}
                </div>
                
                {/* Shodan Intelligence */}
                {quickScanResult.shodan && (quickScanResult.shodan.isp || quickScanResult.shodan.org) && (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">Shodan Intelligence:</p>
                    <div className="space-y-1 text-sm">
                      {quickScanResult.shodan.isp && (
                        <p className="text-slate-300"><span className="text-slate-500">ISP:</span> {quickScanResult.shodan.isp}</p>
                      )}
                      {quickScanResult.shodan.org && (
                        <p className="text-slate-300"><span className="text-slate-500">Org:</span> {quickScanResult.shodan.org}</p>
                      )}
                      {quickScanResult.shodan.location?.country && (
                        <p className="text-slate-300"><span className="text-slate-500">Location:</span> {quickScanResult.shodan.location.city || ''} {quickScanResult.shodan.location.country}</p>
                      )}
                      {quickScanResult.shodan.os && (
                        <p className="text-slate-300"><span className="text-slate-500">OS:</span> {quickScanResult.shodan.os}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Vulnerabilities */}
                {quickScanResult.vulnerabilities?.length > 0 && (
                  <div>
                    <p className="text-red-400 text-sm mb-2">Known Vulnerabilities ({quickScanResult.vulnerabilities.length}):</p>
                    <div className="flex flex-wrap gap-2">
                      {quickScanResult.vulnerabilities.slice(0, 5).map((v, i) => (
                        <span key={i} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
                          {v.cve}
                        </span>
                      ))}
                      {quickScanResult.vulnerabilities.length > 5 && (
                        <span className="px-2 py-1 bg-red-500/10 text-red-300 rounded text-sm">
                          +{quickScanResult.vulnerabilities.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Missing Security Headers */}
                {quickScanResult.headers?.headers_missing?.length > 0 && (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">Missing Security Headers:</p>
                    <div className="flex flex-wrap gap-2">
                      {quickScanResult.headers.headers_missing.map((h, i) => (
                        <span key={i} className={`px-2 py-1 rounded text-sm ${
                          h.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {h.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content - Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Scans List */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h2 className="text-lg font-semibold text-white">Scan History</h2>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {scans.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <Scan className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No scans yet</p>
                    <p className="text-sm">Create your first scan to get started</p>
                  </div>
                ) : (
                  scans.map(scan => (
                    <div
                      key={scan.id}
                      onClick={() => setSelectedScan(scan)}
                      className={`p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors ${
                        selectedScan?.id === scan.id ? 'bg-slate-700/50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${statusColors[scan.status]}`} />
                            <h3 className="text-white font-medium truncate">{scan.name}</h3>
                          </div>
                          <p className="text-slate-400 text-sm truncate mt-1">{scan.target}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 bg-slate-600 rounded text-slate-300">
                              {scan.scanType}
                            </span>
                            {scan.status === 'running' && (
                              <span className="text-xs text-blue-400">{scan.progress}%</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {scan.status === 'pending' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleStartScan(scan.id); }}
                              className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                              title="Start Scan"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          {scan.status === 'running' && (
                            <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteScan(scan.id); }}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                            title="Delete Scan"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Scan Details */}
          <div className="lg:col-span-2">
            {selectedScan ? (
              <ScanDetails scan={selectedScan} onRefresh={loadData} />
            ) : (
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                <Eye className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <h3 className="text-lg font-medium text-slate-400">Select a scan to view details</h3>
                <p className="text-slate-500 text-sm mt-2">
                  Click on any scan from the list to view its results and findings
                </p>
              </div>
            )}
          </div>
        </div>

        {/* New Scan Modal */}
        {showNewScan && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
              <h2 className="text-xl font-bold text-white mb-6">Create New Scan</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Scan Name</label>
                  <input
                    type="text"
                    value={newScan.name}
                    onChange={(e) => setNewScan({...newScan, name: e.target.value})}
                    placeholder="My Security Scan"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Target</label>
                  <input
                    type="text"
                    value={newScan.target}
                    onChange={(e) => setNewScan({...newScan, target: e.target.value})}
                    placeholder="https://example.com or 192.168.1.1"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Target Type</label>
                    <select
                      value={newScan.targetType}
                      onChange={(e) => setNewScan({...newScan, targetType: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="url">URL</option>
                      <option value="host">Host/IP</option>
                      <option value="network">Network Range</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Scan Type</label>
                    <select
                      value={newScan.scanType}
                      onChange={(e) => setNewScan({...newScan, scanType: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="quick">Quick (Top 10 Ports)</option>
                      <option value="full">Full (All Common Ports)</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Scan Options</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'portScan', label: 'Port Scanning' },
                      { key: 'headerCheck', label: 'HTTP Headers' },
                      { key: 'bannerGrab', label: 'Banner Grabbing' },
                      { key: 'sslCheck', label: 'SSL/TLS Check' }
                    ].map(opt => (
                      <label key={opt.key} className="flex items-center gap-2 text-slate-300">
                        <input
                          type="checkbox"
                          checked={newScan[opt.key]}
                          onChange={(e) => setNewScan({...newScan, [opt.key]: e.target.checked})}
                          className="rounded border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowNewScan(false)}
                  className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateScan}
                  disabled={!newScan.name || !newScan.target}
                  className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Create Scan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Scan Details Component
const ScanDetails = ({ scan, onRefresh }) => {
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    shodan: true,
    ports: true,
    vulnerabilities: true,
    headers: false,
    recommendations: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({...prev, [section]: !prev[section]}));
  };

  const results = scan.results || {};
  const summary = results.summary || {};

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 bg-slate-800/50">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{scan.name}</h2>
            <p className="text-slate-400 text-sm mt-1">{scan.target}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm ${statusColors[scan.status]} text-white`}>
              {scan.status}
            </span>
            {scan.status === 'running' && (
              <span className="text-blue-400 text-sm">{scan.progress}%</span>
            )}
          </div>
        </div>
        {scan.status === 'running' && (
          <div className="mt-3">
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${scan.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-h-[500px] overflow-y-auto p-4 space-y-4">
        {/* Summary Section */}
        {scan.status === 'completed' && (
          <div className="bg-slate-700/50 rounded-lg">
            <button
              onClick={() => toggleSection('summary')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-violet-400" />
                Summary
              </h3>
              {expandedSections.summary ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.summary && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold text-white">{summary.openPorts || 0}</p>
                    <p className="text-slate-400 text-sm">Open Ports</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800 rounded-lg">
                    <p className="text-2xl font-bold text-white">{summary.totalVulnerabilities || 0}</p>
                    <p className="text-slate-400 text-sm">Vulnerabilities</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800 rounded-lg">
                    <p className={`text-2xl font-bold ${
                      (summary.riskScore || 0) >= 60 ? 'text-red-400' : 
                      (summary.riskScore || 0) >= 40 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {summary.riskScore || 0}
                    </p>
                    <p className="text-slate-400 text-sm">Risk Score</p>
                  </div>
                  <div className="text-center p-3 bg-slate-800 rounded-lg">
                    <p className={`text-2xl font-bold ${
                      summary.riskLevel === 'Critical' || summary.riskLevel === 'High' ? 'text-red-400' :
                      summary.riskLevel === 'Medium' ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {summary.riskLevel || 'N/A'}
                    </p>
                    <p className="text-slate-400 text-sm">Risk Level</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Shodan Intelligence Section */}
        {results.shodan && (results.shodan.isp || results.shodan.services?.length > 0 || results.shodan.vulns?.length > 0) && (
          <div className="bg-violet-900/30 rounded-lg border border-violet-500/30">
            <button
              onClick={() => toggleSection('shodan')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <Target className="w-4 h-4 text-violet-400" />
                Shodan Intelligence
              </h3>
              {expandedSections.shodan ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.shodan && (
              <div className="px-4 pb-4 space-y-4">
                {/* Host Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {results.shodan.isp && (
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-xs text-slate-500">ISP</p>
                      <p className="text-sm text-white truncate">{results.shodan.isp}</p>
                    </div>
                  )}
                  {results.shodan.org && (
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-xs text-slate-500">Organization</p>
                      <p className="text-sm text-white truncate">{results.shodan.org}</p>
                    </div>
                  )}
                  {results.shodan.location?.country && (
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-xs text-slate-500">Location</p>
                      <p className="text-sm text-white">{results.shodan.location.city || ''} {results.shodan.location.country}</p>
                    </div>
                  )}
                  {results.shodan.os && (
                    <div className="p-2 bg-slate-800/50 rounded">
                      <p className="text-xs text-slate-500">OS</p>
                      <p className="text-sm text-white">{results.shodan.os}</p>
                    </div>
                  )}
                </div>
                
                {/* Shodan-discovered Services */}
                {results.shodan.services?.length > 0 && (
                  <div>
                    <p className="text-sm text-violet-300 mb-2">Detected Services ({results.shodan.services.length})</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {results.shodan.services.slice(0, 6).map((svc, i) => (
                        <div key={i} className="p-2 bg-slate-800/50 rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-violet-400 font-mono">{svc.port}/{svc.transport || 'tcp'}</span>
                            {svc.product && <span className="text-white">{svc.product} {svc.version || ''}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Shodan-discovered Vulns */}
                {results.shodan.vulns?.length > 0 && (
                  <div>
                    <p className="text-sm text-red-400 mb-2">Known CVEs ({results.shodan.vulns.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {results.shodan.vulns.map((cve, i) => (
                        <a 
                          key={i}
                          href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30"
                        >
                          {cve}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Open Ports Section */}
        {results.openPorts?.length > 0 && (
          <div className="bg-slate-700/50 rounded-lg">
            <button
              onClick={() => toggleSection('ports')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <Server className="w-4 h-4 text-green-400" />
                Open Ports ({results.openPorts.length})
              </h3>
              {expandedSections.ports ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.ports && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {results.openPorts.map((port, i) => (
                    <div key={i} className="p-2 bg-slate-800 rounded-lg flex items-center justify-between">
                      <div>
                        <span className="text-green-400 font-mono">{port.port}</span>
                        <span className="text-slate-400 text-sm ml-2">{port.service}</span>
                      </div>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vulnerabilities Section */}
        {results.vulnerabilities?.length > 0 && (
          <div className="bg-slate-700/50 rounded-lg">
            <button
              onClick={() => toggleSection('vulnerabilities')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Vulnerabilities ({results.vulnerabilities.length})
              </h3>
              {expandedSections.vulnerabilities ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.vulnerabilities && (
              <div className="px-4 pb-4 space-y-2">
                {results.vulnerabilities.map((vuln, i) => (
                  <div key={i} className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs ${severityColors[vuln.severity]}`}>
                          {vuln.severity?.toUpperCase()}
                        </span>
                        <h4 className="text-white font-medium mt-1">{vuln.type}</h4>
                        <p className="text-slate-400 text-sm mt-1">{vuln.detail}</p>
                        {vuln.cve && vuln.cve !== 'N/A' && (
                          <p className="text-slate-500 text-xs mt-1">CVE: {vuln.cve}</p>
                        )}
                      </div>
                    </div>
                    {vuln.recommendation && (
                      <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                        <ArrowRight className="w-3 h-3" />
                        {vuln.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Headers Section */}
        {results.headers?.headers_missing?.length > 0 && (
          <div className="bg-slate-700/50 rounded-lg">
            <button
              onClick={() => toggleSection('headers')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" />
                HTTP Headers Analysis
              </h3>
              {expandedSections.headers ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.headers && (
              <div className="px-4 pb-4 space-y-2">
                {results.headers.headers_present?.length > 0 && (
                  <div>
                    <p className="text-green-400 text-sm mb-2">Present ({results.headers.headers_present.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {results.headers.headers_present.map((h, i) => (
                        <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
                          {h.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {results.headers.headers_missing?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-red-400 text-sm mb-2">Missing ({results.headers.headers_missing.length})</p>
                    <div className="space-y-2">
                      {results.headers.headers_missing.map((h, i) => (
                        <div key={i} className="p-2 bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${severityColors[h.severity]}`}>
                              {h.severity}
                            </span>
                            <span className="text-white">{h.name}</span>
                          </div>
                          <p className="text-slate-400 text-xs mt-1">{h.header}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recommendations Section */}
        {results.recommendations?.length > 0 && (
          <div className="bg-slate-700/50 rounded-lg">
            <button
              onClick={() => toggleSection('recommendations')}
              className="w-full p-4 flex items-center justify-between"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-yellow-400" />
                Recommendations ({results.recommendations.length})
              </h3>
              {expandedSections.recommendations ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
            {expandedSections.recommendations && (
              <div className="px-4 pb-4 space-y-2">
                {results.recommendations.map((rec, i) => (
                  <div key={i} className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs mt-0.5 ${
                        rec.priority === 'critical' || rec.priority === 'high' ? 'bg-red-500 text-white' :
                        rec.priority === 'medium' ? 'bg-yellow-500 text-black' : 'bg-blue-500 text-white'
                      }`}>
                        {rec.priority}
                      </span>
                      <div>
                        <h4 className="text-white font-medium">{rec.title}</h4>
                        <p className="text-slate-400 text-sm mt-1">{rec.description}</p>
                        <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" />
                          {rec.remediation}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Results Yet */}
        {scan.status === 'pending' && (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">Scan is pending</p>
            <p className="text-slate-500 text-sm">Click the play button to start the scan</p>
          </div>
        )}

        {scan.status === 'running' && (
          <div className="text-center py-8">
            <RefreshCw className="w-12 h-12 mx-auto text-blue-400 animate-spin mb-3" />
            <p className="text-slate-400">Scan in progress...</p>
            <p className="text-slate-500 text-sm">{scan.progress}% complete</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PentestDashboard;
